import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { 
  Send, Mail, MessageSquare, Users, Clock, CheckCircle,
  AlertCircle, Plus, Search, Filter, Megaphone, Bell, Loader2
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";

const AdminCommunications = () => {
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({
    recipients: "all",
    subject: "",
    message: "",
    specific_user_email: ""
  });
  const [userSearch, setUserSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: "",
    message: "",
    type: "info",
    is_active: true
  });

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [announcementsRes, emailsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/announcements`, getAuthConfig()),
        axios.get(`${API}/admin/email-logs`, getAuthConfig()),
        axios.get(`${API}/admin/users-list`, getAuthConfig())
      ]);
      setAnnouncements(announcementsRes.data.announcements || []);
      setEmailLogs(emailsRes.data.logs || []);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendBulkEmail = async () => {
    if (!emailForm.subject || !emailForm.message) {
      toast.error("Subject and message required");
      return;
    }
    if (emailForm.recipients === "specific_user" && !emailForm.specific_user_email) {
      toast.error("Please select a user");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/send-email`, emailForm, getAuthConfig());
      toast.success(`Email sent to ${res.data.sent_count} user${res.data.sent_count > 1 ? "s" : ""}`);
      setShowEmailDialog(false);
      setEmailForm({ recipients: "all", subject: "", message: "", specific_user_email: "" });
      setUserSearch("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = users.filter(u => {
    if (!userSearch) return true;
    const q = userSearch.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.company_name || "").toLowerCase().includes(q);
  });

  const createAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.message) {
      toast.error("Title and message required");
      return;
    }
    try {
      await axios.post(`${API}/admin/announcements`, announcementForm, getAuthConfig());
      toast.success("Announcement created");
      setShowAnnouncementDialog(false);
      setAnnouncementForm({ title: "", message: "", type: "info", is_active: true });
      fetchData();
    } catch (error) {
      toast.error("Failed to create announcement");
    }
  };

  const toggleAnnouncement = async (id, isActive) => {
    try {
      await axios.patch(`${API}/admin/announcements/${id}`, { is_active: !isActive }, getAuthConfig());
      toast.success(isActive ? "Announcement disabled" : "Announcement enabled");
      fetchData();
    } catch (error) {
      toast.error("Failed to update announcement");
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!confirm("Delete this announcement?")) return;
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, getAuthConfig());
      toast.success("Announcement deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-communications-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">Communications</h1>
          <p className="text-muted-foreground">Manage announcements and bulk communications</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowAnnouncementDialog(true)} variant="outline" className="border-theme text-foreground">
            <Megaphone className="w-4 h-4 mr-2" /> New Announcement
          </Button>
          <Button onClick={() => setShowEmailDialog(true)} className="bg-[#0046BE] text-white">
            <Mail className="w-4 h-4 mr-2" /> Send Email
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="glass-card border-theme">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Users</p>
              <p className="text-xl font-bold text-foreground">{users.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Mail className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Emails Sent</p>
              <p className="text-xl font-bold text-foreground">{emailLogs.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Active Announcements</p>
              <p className="text-xl font-bold text-foreground">{announcements.filter(a => a.is_active).length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="announcements" className="space-y-4">
        <TabsList className="bg-theme-surface border border-theme">
          <TabsTrigger value="announcements" className="data-[state=active]:bg-muted text-foreground">
            <Megaphone className="w-4 h-4 mr-2" /> Announcements
          </TabsTrigger>
          <TabsTrigger value="emails" className="data-[state=active]:bg-muted text-foreground">
            <Mail className="w-4 h-4 mr-2" /> Email History
          </TabsTrigger>
        </TabsList>

        {/* Announcements Tab */}
        <TabsContent value="announcements">
          <Card className="glass-card border-theme">
            <CardHeader>
              <CardTitle className="text-foreground">System Announcements</CardTitle>
              <CardDescription className="text-muted-foreground">
                Announcements shown to users in the dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {announcements.map((a) => (
                  <div key={a.announcement_id} className={`rounded-lg p-4 border ${
                    a.type === "warning" ? "bg-yellow-500/10 border-yellow-500/30" :
                    a.type === "error" ? "bg-red-500/10 border-red-500/30" :
                    a.type === "success" ? "bg-green-500/10 border-green-500/30" :
                    "bg-blue-500/10 border-blue-500/30"
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-foreground font-medium">{a.title}</h3>
                          <Badge className={a.is_active ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}>
                            {a.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <p className="text-muted-foreground text-sm">{a.message}</p>
                        <p className="text-muted-foreground text-xs mt-2">
                          Created: {a.created_at ? format(new Date(a.created_at), "MMM dd, yyyy h:mm a") : "-"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => toggleAnnouncement(a.announcement_id, a.is_active)}
                          className="text-muted-foreground"
                        >
                          {a.is_active ? "Disable" : "Enable"}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteAnnouncement(a.announcement_id)}
                          className="text-red-400"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {announcements.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No announcements</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email History Tab */}
        <TabsContent value="emails">
          <Card className="glass-card border-theme">
            <CardHeader>
              <CardTitle className="text-foreground">Email History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-theme">
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">Date</th>
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">Subject</th>
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">Recipients</th>
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">Sent</th>
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLogs.map((log, i) => (
                      <tr key={i} className="border-b border-theme hover:bg-muted/50">
                        <td className="py-3 px-4 text-muted-foreground">
                          {log.created_at ? format(new Date(log.created_at), "MMM dd, h:mm a") : "-"}
                        </td>
                        <td className="py-3 px-4 text-foreground">{log.subject}</td>
                        <td className="py-3 px-4 text-muted-foreground">{log.recipients}</td>
                        <td className="py-3 px-4 text-muted-foreground">{log.sent_count || 0}</td>
                        <td className="py-3 px-4">
                          <Badge className={log.status === "sent" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                            {log.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {emailLogs.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No emails sent yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-background border-theme max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-foreground">Send Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-muted-foreground text-sm">Recipients</label>
              <Select value={emailForm.recipients} onValueChange={(v) => {
                setEmailForm({...emailForm, recipients: v, specific_user_email: ""});
                setUserSearch("");
              }}>
                <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1" data-testid="email-recipients-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-theme">
                  <SelectItem value="all">All Users ({users.length})</SelectItem>
                  <SelectItem value="active">Active Subscribers</SelectItem>
                  <SelectItem value="trial">Trial Users</SelectItem>
                  <SelectItem value="specific_user">Specific User</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {emailForm.recipients === "specific_user" && (
              <div>
                <label className="text-muted-foreground text-sm">Search User</label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search by name, email, or company..."
                    className="bg-theme-surface border-theme text-foreground pl-9"
                    data-testid="email-user-search-input"
                  />
                </div>
                {emailForm.specific_user_email && (
                  <div className="mt-2 flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2">
                    <CheckCircle className="w-4 h-4 text-blue-400 shrink-0" />
                    <span className="text-foreground text-sm truncate">{emailForm.specific_user_email}</span>
                    <button onClick={() => setEmailForm({...emailForm, specific_user_email: ""})} className="ml-auto text-muted-foreground hover:text-foreground">
                      <AlertCircle className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {!emailForm.specific_user_email && userSearch && (
                  <div className="mt-2 max-h-40 overflow-y-auto overscroll-contain touch-pan-y rounded-lg border border-theme bg-theme-surface" data-testid="email-user-search-results">
                    {filteredUsers.slice(0, 10).map((u) => (
                      <button
                        key={u.email}
                        onClick={() => {
                          setEmailForm({...emailForm, specific_user_email: u.email});
                          setUserSearch("");
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-muted/50 flex items-center gap-3 border-b border-theme last:border-0"
                        data-testid={`email-user-option-${u.email}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                          <span className="text-blue-400 text-xs font-bold">{(u.name || u.email || "?")[0].toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-foreground text-sm font-medium truncate">{u.name || "—"}</p>
                          <p className="text-muted-foreground text-xs truncate">{u.email}{u.company_name ? ` · ${u.company_name}` : ""}</p>
                        </div>
                      </button>
                    ))}
                    {filteredUsers.length === 0 && (
                      <p className="text-center text-muted-foreground text-sm py-4">No users found</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-muted-foreground text-sm">Subject</label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                placeholder="Email subject..."
                className="bg-theme-surface border-theme text-foreground mt-1"
                data-testid="email-subject-input"
              />
            </div>
            <div>
              <label className="text-muted-foreground text-sm">Message</label>
              <Textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                placeholder="Email content..."
                rows={6}
                className="bg-theme-surface border-theme text-foreground mt-1"
                data-testid="email-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEmailDialog(false)} className="text-foreground">Cancel</Button>
            <Button onClick={sendBulkEmail} className="bg-[#0046BE] text-white" disabled={sending} data-testid="send-email-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sending ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Announcement Dialog */}
      <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <DialogContent className="bg-background border-theme">
          <DialogHeader>
            <DialogTitle className="text-foreground">New Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-muted-foreground text-sm">Type</label>
              <Select value={announcementForm.type} onValueChange={(v) => setAnnouncementForm({...announcementForm, type: v})}>
                <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-theme">
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-muted-foreground text-sm">Title</label>
              <Input
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                placeholder="Announcement title..."
                className="bg-theme-surface border-theme text-foreground mt-1"
              />
            </div>
            <div>
              <label className="text-muted-foreground text-sm">Message</label>
              <Textarea
                value={announcementForm.message}
                onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
                placeholder="Announcement message..."
                rows={4}
                className="bg-theme-surface border-theme text-foreground mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAnnouncementDialog(false)} className="text-foreground">Cancel</Button>
            <Button onClick={createAnnouncement} className="bg-[#0046BE] text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCommunications;
